import React from 'react';
import {
  Button,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import type { ExternalRunDetailSearchParams } from '../../../router';

const VERDICT_LABELS: Record<string, string> = {
  passed: 'Passed',
  failed: 'Failed',
  flaky: 'Flaky',
  skipped: 'Skipped',
};

/** Shared empty array so the no-verdict-filter case keeps a stable reference. */
const EMPTY_VERDICTS: string[] = [];

export type ExternalTestsFilterBarProps = {
  filters: ExternalRunDetailSearchParams;
  onFilterChange: (updates: Partial<ExternalRunDetailSearchParams>) => void;
  verdictOptions: MultiSelectOption[];
  /** Fuse with the table below: square bottom corners, no bottom border. */
  attached?: boolean;
};

/**
 * The external run results toolbar: free-text search over the scenario
 * label plus a verdict filter, mirroring `AgenticRunFailuresTable`'s toolbar.
 * Every filter change resets to page 1 — a filter narrows the result set, so
 * whatever page the reader was on may no longer exist.
 */
export function ExternalTestsFilterBar({
  filters,
  onFilterChange,
  verdictOptions,
  attached = false,
}: ExternalTestsFilterBarProps) {
  const [searchInput, setSearchInput] = React.useState(filters.q ?? '');

  React.useEffect(() => {
    setSearchInput(filters.q ?? '');
  }, [filters.q]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if ((filters.q ?? '') === trimmed) return;
      onFilterChange({ q: trimmed || undefined, page: 1 });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput, filters.q, onFilterChange]);

  const selectedVerdicts = filters.verdicts ?? EMPTY_VERDICTS;

  const chips = React.useMemo<FilterToolbarChip[]>(() => {
    const list: FilterToolbarChip[] = [];
    if (filters.q) list.push({ id: 'q', label: `Search: ${filters.q}` });
    selectedVerdicts.forEach((verdict) => {
      list.push({
        id: `verdict:${verdict}`,
        label: `Status: ${VERDICT_LABELS[verdict] ?? verdict}`,
      });
    });
    return list;
  }, [filters.q, selectedVerdicts]);

  const hasActiveFilters = chips.length > 0;

  const handleClearAll = React.useCallback(() => {
    setSearchInput('');
    onFilterChange({ q: undefined, verdicts: undefined, page: 1 });
  }, [onFilterChange]);

  const handleChipRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'q') {
        setSearchInput('');
        onFilterChange({ q: undefined, page: 1 });
      } else if (chipId.startsWith('verdict:')) {
        const verdict = chipId.split(':', 2)[1];
        const next = selectedVerdicts.filter((v) => v !== verdict);
        onFilterChange({ verdicts: next.length > 0 ? next : undefined, page: 1 });
      }
    },
    [onFilterChange, selectedVerdicts]
  );

  return (
    <FilterToolbar attached={attached}>
      <FilterToolbarSearch
        aria-label="Search tests"
        placeholder="Search tests..."
        value={searchInput}
        onChange={setSearchInput}
      />

      <ToolbarFilterMenu
        label="Status"
        options={verdictOptions}
        selectedIds={selectedVerdicts}
        onChange={(ids) => onFilterChange({ verdicts: ids.length > 0 ? ids : undefined, page: 1 })}
        searchable={false}
      />

      <FilterToolbarDivider />

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onPress={handleClearAll}>
          Clear
        </Button>
      )}

      <FilterToolbarChips chips={chips} onRemove={handleChipRemove} />
    </FilterToolbar>
  );
}
